import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const ArtLightbox = ({ active, onClose, testid = "art-lightbox" }) => {
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      window.__lenis?.start();
    };
  }, [active, onClose]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          data-testid={testid}
          role="dialog"
          aria-modal="true"
          aria-label={`${active.label} full-screen artwork viewer`}
          className="fixed inset-0 z-[140] w-screen h-[100dvh] flex items-center justify-center cursor-zoom-out"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          style={{ background: "rgba(0,0,0,0.94)", backdropFilter: "blur(10px)", overscrollBehavior: "contain" }}
          onClick={onClose}
        >
          <button
            type="button"
            data-testid={`${testid}-close`}
            aria-label="Close artwork view"
            onClick={onClose}
            className="fixed top-4 right-4 z-10 w-11 h-11 flex items-center justify-center bg-black/55 border border-[var(--cb-gold)]/45 text-[var(--cb-gold)] hover:bg-[var(--cb-gold)] hover:text-black transition-colors duration-200 cursor-pointer"
          >
            <X size={24} strokeWidth={1.8} />
          </button>

          <motion.figure
            initial={{ scale: 0.97, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.985, opacity: 0 }}
            transition={{ duration: 0.22, ease: EASE }}
            className="relative w-full h-full flex flex-col items-center justify-center p-4 md:p-8"
          >
            <img
              src={active.src}
              alt={`${active.label}${active.sub ? ` — ${active.sub}` : ""}`}
              className="block w-auto h-auto object-contain border border-[var(--cb-gold)]/30 select-none"
              style={{
                maxWidth: "min(100vw, 1920px)",
                maxHeight: "calc(min(100dvh, 1080px) - 72px)",
                touchAction: "pinch-zoom",
              }}
              draggable="false"
              onClick={(e) => e.stopPropagation()}
            />
            <figcaption className="text-center mt-4 shrink-0" onClick={(e) => e.stopPropagation()}>
              <div className="font-caps text-[10px] text-[var(--cb-gold)]">{active.label}</div>
              {active.sub && (
                <div className="font-headline text-[var(--cb-text)] text-[15px] uppercase tracking-[0.03em] mt-1.5">{active.sub}</div>
              )}
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ArtLightbox;
